import { SparkSuspense } from '@sparklayer-io/spark-dash-core/components/spark-suspense';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { ExternalLink, Icon, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useSyncErrors } from '../hooks/use-sync-log-errors';
import type { SyncError, SyncErrorFilter, SyncLogIdentifier } from '../types';

const PAGE_SIZE = 10;

export function SyncLogErrorTable({
  syncFor,
  filter,
  showApiErrors,
  searchValue,
}: {
  syncFor: SyncLogIdentifier;
  filter?: SyncErrorFilter;
  showApiErrors: boolean;
  searchValue?: string;
}): React.ReactNode {
  const {
    data: syncStatusWithErrors,
    isError,
    isLoading,
    refetch,
  } = useSyncErrors(syncFor, filter);
  const [page, setPage] = useState<number>(0);
  const [prevSearchValue, setPrevSearchValue] = useState<string | undefined>(searchValue);

  if (searchValue !== prevSearchValue) {
    setPage(0);
    setPrevSearchValue(searchValue);
  }

  let errors = syncStatusWithErrors?.errors ?? [];
  if (searchValue && searchValue.length >= 2) {
    const searchLower = searchValue.toLowerCase();
    errors = errors.filter((error) => error.external_id.toLowerCase().includes(searchLower));
  }

  const total = errors.length;
  const pageErrors = errors.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <SparkSuspense
      isError={isError}
      isLoading={isLoading}
      onReload={refetch}
      errorMessage={t('features:synclog.components.sync_log_error_table.load_error')}
    >
      <Table
        headings={[
          t('features:synclog.components.sync_log_error_table.headings.name'),
          t('features:synclog.components.sync_log_error_table.headings.description'),
        ]}
        caption={t('features:synclog.components.sync_log_error_table.caption')}
        pagination={{
          hasPrevious: page > 0,
          hasNext: (page + 1) * PAGE_SIZE < total,
          onPrevious: () => setPage((p) => p - 1),
          onNext: () => setPage((p) => p + 1),
          counts: {
            first: page * PAGE_SIZE + 1,
            last: Math.min((page + 1) * PAGE_SIZE, total),
            total,
          },
        }}
      >
        {pageErrors.map((error, index) => (
          <SyncErrorTableRow
            // An entity can have an error under each of its parents.
            key={`${error.external_id}:${error.parent_external_id ?? ''}`}
            syncError={error}
            index={index}
            showApiErrors={showApiErrors}
          />
        ))}
      </Table>
    </SparkSuspense>
  );
}

function SyncErrorTableRow({
  syncError,
  index,
  showApiErrors,
}: {
  syncError: SyncError;
  index: number;
  showApiErrors: boolean;
}) {
  const hasUrl = syncError.external_url !== '';

  return (
    <Table.Row index={index} id={syncError.external_id}>
      <Table.Cell>
        {hasUrl ? (
          <ExternalLink link={syncError.external_url}>
            {syncError.visible_name} <Icon name="externalLink" />
          </ExternalLink>
        ) : (
          syncError.visible_name
        )}
      </Table.Cell>
      <Table.Cell>
        <span className={cn('whitespace-normal')}>
          <Text size="md" variant="primary">
            {syncError.friendly_error}
          </Text>
          {showApiErrors && (
            <span className={cn('font-mono')}>
              <Text size="sm" variant="secondary">
                {syncError.api_response}
              </Text>
            </span>
          )}
        </span>
      </Table.Cell>
    </Table.Row>
  );
}
